import React, { useEffect, useState } from 'react';
import { useGetInternalUsersQuery, useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';
import { CircleAlert } from 'lucide-react';
import { Company } from 'src/pages/billing/types/billing.types';
import { useGetSKUsQuery } from 'src/redux/billing/billing.endpoints';
import BorderedToggle from 'src/components/form-new/bordered-toggle';
import BorderedCompanySelect from 'src/components/form-new/bordered-company-select';
import BorderedSkuSelect from 'src/components/form-new/bordered-sku-select';
import BorderedUserSelect from 'src/components/form-new/bordered-user-select';
import { useDispatch } from 'react-redux';
import notificationService from 'src/services/notification/notification.service';

interface CampaignTableProps {
  campaigns: Campaign[];
  companies: Company[];
  filter?: 'attention_needed' | 'all';
  isRefreshing?: boolean;
  className?: string;
  searchQuery: string;
  onUpdateCampaign?: (campaignId: string, field: string, value: any) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  isRefreshing,
  className,
  filter = 'all',
  campaigns,
  companies,
  searchQuery,
  onUpdateCampaign,
}) => {
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const { data: skus = [] } = useGetSKUsQuery();
  const { data: users = [] } = useGetInternalUsersQuery();

  useEffect(() => {
    setIsLoading(isRefreshing || isUpdating);
  }, [isRefreshing, isUpdating]);

  useEffect(() => {
    setFilteredCampaigns(campaigns.filter((campaign: Campaign) => {
      if (filter === 'attention_needed') {
        if (searchQuery.trim()) {
          return campaign.attention_needed && (campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return campaign.attention_needed;
      }

      if (searchQuery.trim()) {
        return campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    }));
  }, [filter, campaigns, searchQuery]);

  const handleUpdateCampaign = async(campaignId: string, field: string, value: any) => {
    try {
      await updateCampaign({
        campaignId,
        [field]: value,
      }).unwrap();
      onUpdateCampaign?.(campaignId, field, value);
      notificationService.handleSuccess('Campaign updated successfully', dispatch, 'Campaign updated successfully');
    } catch (error) {
      console.error('Failed to update campaign:', error);
      onUpdateCampaign?.(campaignId, field, value);
      notificationService.handleError(error, dispatch, 'Failed to update campaign');
    }
  };

  useEffect(() => {
    console.log('isLoading', isLoading);
  }, [isLoading]);

  return (
    <div className={`overflow-y-scroll w-full ${className}`}>

      <div className={`grid grid-cols-2 gap-4 w-full ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {filteredCampaigns.map((campaign: Campaign) => (
          <div
            className='bg-white flex flex-col justify-between rounded-lg p-4 w-full relative'
            key={campaign.id}
          >
            {campaign.attention_needed && (
              <div className='absolute top-2 right-2'>
                <button className='text-red-500 bg-white rounded-full opacity-80 cursor-auto'>
                  <CircleAlert className='size-5' />
                </button>
              </div>
            )}
            <div className='text-lg font-normal'>
              {campaign.title}
            </div>
            <div className='flex flex-row gap-2 flex-wrap justify-between'>
              <div className='text-xs text-gray-500'>
                {campaign.account.title}
                {' '}
                |
                {' '}
                {campaign.account?.source?.toLocaleLowerCase().replace('_', ' ')}
              </div>

            </div>
            <div className='flex gap-2 mt-4 border-b border-gray-200 pb-1'>
              <BorderedCompanySelect
                companies={companies}
                value={campaign.company || ''}
                onChange={(companyId) => handleUpdateCampaign(campaign.id, 'company', companyId)}
                label="Company"
                suggestionQuery={campaign.title}
                campaignAccountCompanyId={campaign.account?.company || ''}
                attentionNeeded={campaign.attention_needed}
              />

              <BorderedSkuSelect
                skus={skus}
                value={campaign.sku || ''}
                onChange={(skuId) => handleUpdateCampaign(campaign.id, 'sku', skuId)}
                label="SKU"
                campaignTitle={campaign.title}
                attentionNeeded={campaign.attention_needed}
              />

              <BorderedUserSelect
                users={users}
                value={campaign.owner || ''}
                onChange={(userId) => handleUpdateCampaign(campaign.id, 'owner', userId)}
                label="Owner"
                attentionNeeded={campaign.attention_needed}
              />
            </div>
            <div className='flex gap-2 mt-5'>
              <BorderedToggle
                label="Pre-Billed"
                trueLabel='Pre-Billed'
                falseLabel='Not Pre-Billed'
                value={campaign.pre_billed}
                onChange={() => handleUpdateCampaign(campaign.id, 'pre_billed', !campaign.pre_billed)}
              />

              <BorderedToggle
                label="Do Not Bill"
                trueLabel='Bill'
                falseLabel='Do Not Bill'
                value={!campaign.do_not_bill}
                onChange={() => handleUpdateCampaign(campaign.id, 'do_not_bill', !campaign.do_not_bill)}
              />

              <BorderedToggle
                label="Hide"
                trueLabel='Hidden'
                falseLabel='Not Hidden'
                value={campaign.hide || false}
                onChange={() => handleUpdateCampaign(campaign.id, 'hide', !campaign.hide)}
              />
            </div>
            <div />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignTable;
